"use client";

import { CodeInput } from "@/app/(front-door)/_components/CodeInput";
import { FrontDoorNotice } from "@/app/(front-door)/_components/FrontDoorNotice";
import {
  FRONT_DOOR_FIELD_STYLES,
  FRONT_DOOR_HELP_CLASS,
  FRONT_DOOR_LINK_CLASS,
  FRONT_DOOR_PRIMARY_BUTTON_CLASS,
  FRONT_DOOR_QUIET_BUTTON_CLASS,
  FRONT_DOOR_SECONDARY_BUTTON_CLASS,
} from "@/app/(front-door)/_components/styles";
import { Button } from "@/app/_components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_components/ui/form";

import { Input } from "@/app/_components/ui/input";
import { PhoneInput } from "@/app/_components/ui/phone-input";
import {
  handlePortalOtpActionFailure,
  PORTAL_OTP_PHONE_MULTIPLE_ACCOUNTS_MESSAGE,
  logPortalOtpInfo,
  logPortalOtpWarn,
  setUnexpectedOtpError,
  toPortalOtpLogMethod,
} from "@/app/(portal)/portal/_components/portal-otp-login-handlers";
import {
  sendOtpToPortalUser,
  sendOtpToPortalUserEmail,
  verifyEmailOtpCode,
  verifySmsOtpCode,
} from "@/lib/modules/auth/actions";
import { useMarblePhoneNumber } from "@/lib/hooks/useMarblePhoneNumber";
import { getPortalRedirectUrl, PortalParams } from "@/lib/portal-params";
import { datadogRum } from "@datadog/browser-rum";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const PhoneNumberSchema = z.object({
  phoneNumber: z.string().min(1, "Please enter a valid phone number"),
});

const EmailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

const OtpSchema = z.object({
  otpCode: z.string().min(6, "Please enter the 6-digit code"),
});

type PhoneNumberFormData = z.infer<typeof PhoneNumberSchema>;
type EmailFormData = z.infer<typeof EmailSchema>;
type OtpFormData = z.infer<typeof OtpSchema>;

export function PortalOtpLoginForm({
  userId,
  therapistId,
  type,
}: {
  userId?: string;
  therapistId?: string;
  type?: string;
}) {
  const [step, setStep] = useState<"phone" | "email" | "otp">("phone");
  const [authMethod, setAuthMethod] = useState<"phone" | "email">("phone");
  const [contactInfo, setContactInfo] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [multipleAccountsError, setMultipleAccountsError] = useState<
    string | null
  >(null);
  const marblePhoneNumber = useMarblePhoneNumber({});
  const marblePhoneNumberFormatted = useMarblePhoneNumber({
    formatted: true,
  });

  const phoneForm = useForm<PhoneNumberFormData>({
    resolver: zodResolver(PhoneNumberSchema),
    defaultValues: {
      phoneNumber: "",
    },
  });

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(EmailSchema),
    defaultValues: {
      email: "",
    },
  });

  const otpForm = useForm<OtpFormData>({
    resolver: zodResolver(OtpSchema),
    defaultValues: {
      otpCode: "",
    },
  });

  const resetContactForms = () => {
    phoneForm.reset();
    emailForm.reset();
    otpForm.reset();
  };

  const onSubmitPhoneNumber = async (values: PhoneNumberFormData) => {
    setLoading(true);
    setError(null);
    setMultipleAccountsError(null);
    setContactInfo(values.phoneNumber);
    const sessionId = _getDatadogSessionId();

    try {
      const result = await sendOtpToPortalUser(values.phoneNumber, sessionId);

      if (result?.success) {
        logPortalOtpInfo(
          "SMS OTP sent successfully for portal login",
          "sms",
          { phone: values.phoneNumber },
          { status: "otp_sent" },
        );
        setStep("otp");
      } else {
        handlePortalOtpActionFailure({
          result: result ?? {},
          setError,
          setMultipleAccountsError,
          switchToEmailDeps: {
            setAuthMethod,
            setStep,
            setContactInfo,
            resetContactForms,
          },
          switchToEmailLogMessage:
            "Multiple accounts detected, switching to email",
          switchToEmailMethod: "sms",
          switchToEmailContact: { phone: values.phoneNumber },
          emailMultipleLogMessage:
            "Multiple accounts detected for email (unexpected after phone send)",
          emailMultipleMethod: "sms",
          emailMultipleContact: { phone: values.phoneNumber },
          genericFailure: {
            method: "sms",
            contact: { phone: values.phoneNumber },
            logMessage: "SMS OTP sending failed for portal login",
            status: "otp_failed",
            fallbackMessage: "Failed to send verification code",
          },
        });
      }
    } catch (err) {
      setUnexpectedOtpError(
        err,
        "sms",
        { phone: values.phoneNumber },
        "SMS OTP sending error for portal login",
        setError,
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmitEmail = async (values: EmailFormData) => {
    setLoading(true);
    setError(null);
    setMultipleAccountsError(null);
    setContactInfo(values.email);
    const sessionId = _getDatadogSessionId();
    try {
      const result = await sendOtpToPortalUserEmail(values.email, sessionId);

      if (result?.success) {
        logPortalOtpInfo(
          "Email OTP sent successfully for portal login",
          "email",
          { email: values.email },
          { status: "otp_sent" },
        );
        setStep("otp");
      } else {
        handlePortalOtpActionFailure({
          result: result ?? {},
          setError,
          setMultipleAccountsError,
          switchToEmailDeps: {
            setAuthMethod,
            setStep,
            setContactInfo,
            resetContactForms,
          },
          switchToEmailLogMessage:
            "Multiple accounts for phone (unexpected after email send)",
          switchToEmailMethod: "email",
          switchToEmailContact: { email: values.email },
          emailMultipleLogMessage: "Multiple accounts detected for email",
          emailMultipleMethod: "email",
          emailMultipleContact: { email: values.email },
          genericFailure: {
            method: "email",
            contact: { email: values.email },
            logMessage: "Email OTP sending failed for portal login",
            status: "otp_failed",
            fallbackMessage: "Failed to send verification code",
          },
        });
      }
    } catch (err) {
      setUnexpectedOtpError(
        err,
        "email",
        { email: values.email },
        "Email OTP sending error for login",
        setError,
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmitOtp = async (values: OtpFormData) => {
    setLoading(true);
    setError(null);
    setMultipleAccountsError(null);
    const sessionId = _getDatadogSessionId();
    const method = toPortalOtpLogMethod(authMethod);
    const contact =
      authMethod === "phone"
        ? { phoneNumber: contactInfo, contactInfo }
        : { email: contactInfo, contactInfo };

    try {
      let result;
      let signInResponse;

      if (authMethod === "phone") {
        result = await verifySmsOtpCode(values.otpCode, contactInfo, sessionId);
        if (result?.success && result?.userId) {
          logPortalOtpInfo(
            "SMS OTP verified, attempting sign in",
            "sms",
            { phoneNumber: contactInfo },
            {
              userId: result?.userId,
              step: "signin_attempt",
            },
          );

          signInResponse = await signIn("sms-otp", {
            otp: values.otpCode,
            phoneNumber: contactInfo,
            redirect: false,
          });
        }
      } else {
        result = await verifyEmailOtpCode(
          values.otpCode,
          contactInfo,
          sessionId,
        );
        if (result?.success && result?.userId) {
          logPortalOtpInfo(
            "Email OTP verified, attempting sign in",
            "email",
            { email: contactInfo },
            {
              userId: result?.userId,
              step: "signin_attempt",
            },
          );

          signInResponse = await signIn("email-otp", {
            otp: values.otpCode,
            email: contactInfo,
            redirect: false,
          });
        }
      }

      if (result?.success && result?.userId && signInResponse?.ok) {
        logPortalOtpInfo("Portal login successful", method, contact, {
          userId: result?.userId,
          status: "success",
        });

        const portalParams: PortalParams = {
          userId,
          therapistId,
          type,
        };

        const redirectUrl = getPortalRedirectUrl(portalParams);
        window.location.assign(redirectUrl);
      }

      if (signInResponse?.error) {
        logPortalOtpWarn(
          "Portal sign in failed after OTP verification",
          "signin_failed",
          method,
          contact,
          {
            userId: result?.userId,
            error: signInResponse.error,
          },
        );
        setError(signInResponse.error);
      } else if (!result?.success) {
        handlePortalOtpActionFailure({
          result: result ?? {},
          setError,
          setMultipleAccountsError,
          switchToEmailDeps: {
            setAuthMethod,
            setStep,
            setContactInfo,
            resetContactForms,
          },
          switchToEmailLogMessage:
            "Multiple accounts detected during OTP verification, switching to email",
          switchToEmailMethod: method,
          switchToEmailContact: contact,
          emailMultipleLogMessage:
            "Multiple accounts detected for email during OTP verification",
          emailMultipleMethod: method,
          emailMultipleContact: contact,
          genericFailure: {
            method,
            contact,
            logMessage: "Portal OTP verification failed",
            status: "otp_verification_failed",
            fallbackMessage: "Invalid verification code",
          },
        });
      }
    } catch (err) {
      setUnexpectedOtpError(
        err,
        method,
        contact,
        "Portal OTP verification error",
        setError,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError(null);
    setMultipleAccountsError(null);
    const sessionId = _getDatadogSessionId();
    const method = toPortalOtpLogMethod(authMethod);

    logPortalOtpInfo(
      "Portal OTP resend requested",
      method,
      { contactInfo },
      { step: "resend_otp" },
    );

    try {
      let result;
      if (authMethod === "phone") {
        result = await sendOtpToPortalUser(contactInfo, sessionId);
      } else {
        result = await sendOtpToPortalUserEmail(contactInfo, sessionId);
      }

      if (result?.error) {
        handlePortalOtpActionFailure({
          result,
          setError,
          setMultipleAccountsError,
          switchToEmailDeps: {
            setAuthMethod,
            setStep,
            setContactInfo,
            resetContactForms,
          },
          switchToEmailLogMessage:
            "Multiple accounts detected during resend, switching to email",
          switchToEmailMethod: method,
          switchToEmailContact: { contactInfo },
          emailMultipleLogMessage:
            "Multiple accounts detected for email during resend",
          emailMultipleMethod: method,
          emailMultipleContact: { contactInfo },
          genericFailure: {
            method,
            contact: { contactInfo },
            logMessage: "Portal OTP resend failed",
            status: "resend_failed",
            fallbackMessage: "Failed to resend verification code",
          },
        });
      } else {
        logPortalOtpInfo(
          "Portal OTP resend successful",
          method,
          { contactInfo },
          { status: "resend_success" },
        );
      }
    } catch (err) {
      setUnexpectedOtpError(
        err,
        method,
        { contactInfo },
        "Portal OTP resend error",
        setError,
      );
    } finally {
      setLoading(false);
    }
  };

  const goBackToContact = () => {
    setStep(authMethod);
    setError(null);
    setMultipleAccountsError(null);
  };

  const switchToEmail = () => {
    setContactInfo("");
    setAuthMethod("email");
    setStep("email");
    setError(null);
    setMultipleAccountsError(null);
    phoneForm.reset();
    emailForm.reset();
  };

  const switchToPhone = () => {
    setContactInfo("");
    setAuthMethod("phone");
    setStep("phone");
    setError(null);
    setMultipleAccountsError(null);
    phoneForm.reset();
    emailForm.reset();
  };

  const emailStep = (
    <Form {...emailForm}>
      <form
        onSubmit={emailForm.handleSubmit(onSubmitEmail)}
        className={FRONT_DOOR_FIELD_STYLES}
      >
        <FormField
          control={emailForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  autoComplete="email"
                  placeholder="your.email@example.com"
                  disabled={loading}
                />
              </FormControl>
              <p className={FRONT_DOOR_HELP_CLASS}>
                Please enter the email address you use to communicate with
                Marble Health.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant="custom"
          className={`mt-5 ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send verification code"}
        </Button>
      </form>
      <div className="mt-3.5 text-center">
        <button
          type="button"
          onClick={switchToPhone}
          className={`text-sm ${FRONT_DOOR_LINK_CLASS}`}
        >
          Use phone number instead
        </button>
      </div>
    </Form>
  );

  const phoneStep = (
    <Form {...phoneForm}>
      <form
        onSubmit={phoneForm.handleSubmit(onSubmitPhoneNumber)}
        className={FRONT_DOOR_FIELD_STYLES}
      >
        <FormField
          control={phoneForm.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone number</FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  type="tel"
                  autoComplete="tel"
                  placeholder="(888) 888-8888"
                  disabled={loading}
                />
              </FormControl>
              <p className={FRONT_DOOR_HELP_CLASS}>
                Please enter the phone number you use to communicate with Marble
                Health.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant="custom"
          className={`mt-5 ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send verification code"}
        </Button>
      </form>
      <div className="mt-3.5 text-center">
        <button
          type="button"
          onClick={switchToEmail}
          className={`text-sm ${FRONT_DOOR_LINK_CLASS}`}
        >
          Use email instead
        </button>
      </div>
    </Form>
  );

  const hasCodeError = step === "otp" && !!error;
  const otpStep = (
    <Form {...otpForm}>
      <form onSubmit={otpForm.handleSubmit(onSubmitOtp)}>
        <p className="mb-5 text-center text-[15px] leading-normal text-[#4a4a4a]">
          {"Enter the 6-digit code sent to "}
          <strong className="break-all text-[#1a1a1a]">{contactInfo}</strong>
        </p>

        <FormField
          control={otpForm.control}
          name="otpCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[15px] font-semibold text-[#1a1a1a]">
                Verification code
              </FormLabel>
              <FormControl>
                <CodeInput
                  {...field}
                  disabled={loading}
                  hasError={hasCodeError}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant="custom"
          className={`mt-[22px] ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
          disabled={loading}
        >
          {loading ? "Verifying..." : "Verify code"}
        </Button>
        <Button
          type="button"
          variant="custom"
          className={`mt-2.5 ${FRONT_DOOR_SECONDARY_BUTTON_CLASS}`}
          onClick={handleResendOtp}
          disabled={loading}
        >
          Resend code
        </Button>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={goBackToContact}
            disabled={loading}
            className={FRONT_DOOR_QUIET_BUTTON_CLASS}
          >
            {authMethod === "phone" ? "Back to phone number" : "Back to email"}
          </button>
        </div>
      </form>
    </Form>
  );

  const getStep = () => {
    if (step === "phone") {
      return phoneStep;
    } else if (step === "email") {
      return emailStep;
    } else if (step === "otp") {
      return otpStep;
    }
  };

  const bannerMessage = multipleAccountsError || error;

  return (
    <div>
      {bannerMessage && (
        <FrontDoorNotice
          tone={
            bannerMessage === PORTAL_OTP_PHONE_MULTIPLE_ACCOUNTS_MESSAGE
              ? "warning"
              : "error"
          }
          className="mb-[18px]"
        >
          {bannerMessage}
        </FrontDoorNotice>
      )}
      {getStep()}
      <p className={`mt-[18px] text-center ${FRONT_DOOR_HELP_CLASS}`}>
        {"No password needed. Trouble logging in? Text us at "}
        <a href={`sms:${marblePhoneNumber}`} className={FRONT_DOOR_LINK_CLASS}>
          {marblePhoneNumberFormatted}
        </a>
      </p>
    </div>
  );
}

function _getDatadogSessionId(): string | undefined {
  if (!process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
    return undefined;
  }

  try {
    return datadogRum.getInternalContext()?.session_id;
  } catch {
    return undefined;
  }
}
